import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy, Package, ShoppingBag, Store, Truck, Home, ChefHat } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import WaferField from "@/components/WaferField";

const STEPS = [
    { key: "placed", label: "Order placed", note: "We've received your order", icon: CheckCircle2 },
    { key: "preparing", label: "Preparing", note: "Handcrafting your pudding", icon: ChefHat },
    { key: "ready", label: "Ready / Shipped", note: "On its way to you", icon: Package },
    { key: "delivered", label: "Delivered", note: "Enjoy every spoonful", icon: Home },
];

function stepIndex(status = "") {
    const s = status.toLowerCase();
    if (s.includes("deliver")) return 3;
    if (s.includes("ready") || s.includes("ship") || s.includes("out for")) return 2;
    if (s.includes("prepar") || s.includes("confirm")) return 1;
    return 0;
}

const FULFILL_ICON = { pickup: Store, delivery: Truck, shipping: Package };

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { clear } = useCart();
    const [status, setStatus] = useState("checking");
    const [order, setOrder] = useState(null);
    const attempts = useRef(0);
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) { setStatus("error"); return; }
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    setOrder(data.order);
                    if (!cleared.current) { clear(); cleared.current = true; }
                    return;
                }
                if (data.payment_status === "expired" || attempts.current > 8) { setStatus("error"); return; }
            } catch {
                if (attempts.current > 8) { setStatus("error"); return; }
            }
            attempts.current += 1;
            timer = setTimeout(poll, 2000);
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId, clear]);

    if (status === "checking") return <Skeleton />;
    if (status === "error") return <ErrorState />;

    const active = stepIndex(order?.tracking_status);
    const FIcon = FULFILL_ICON[order?.fulfillment] || Package;
    const items = (order?.items || []).filter((i) => i.kind === "item" || i.line_total != null);

    return (
        <div className="relative min-h-screen overflow-hidden bg-cream-50" data-testid="payment-success">
            <WaferField />
            <div className="relative lg:grid lg:min-h-screen lg:grid-cols-12">
                {/* LEFT — confirmation + timeline */}
                <div className="col-span-7 flex flex-col justify-center gap-10 px-6 py-14 sm:px-10 lg:py-20 lg:pl-24 lg:pr-16">
                    <div className="sp-anim-in" style={{ animationDelay: "40ms" }}>
                        <span className="grid h-16 w-16 place-items-center rounded-full bg-banana/25 ring-1 ring-banana/40">
                            <CheckCircle2 size={34} className="text-caramel" strokeWidth={2.2} />
                        </span>
                        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-caramel">Order confirmed</p>
                        <h1 className="mt-2 font-serif text-4xl leading-tight tracking-tight text-roast md:text-5xl">
                            Thank you, <span className="italic text-caramel">{(order?.name || "friend").split(" ")[0]}</span>.
                            <br />Your pudding is on the way.
                        </h1>
                        <p className="mt-3 max-w-md text-base leading-relaxed text-mocha">
                            We're already prepping your order with love. A confirmation has been sent to{" "}
                            <span className="font-medium text-roast">{order?.email}</span>.
                        </p>
                    </div>

                    {/* Order number */}
                    <div className="sp-anim-in flex items-center justify-between rounded-2xl border-l-4 border-banana bg-cream-200/60 py-4 pl-5 pr-3" style={{ animationDelay: "120ms" }}>
                        <div>
                            <p className="text-xs uppercase tracking-wide text-mocha">Order number</p>
                            <p data-testid="success-order-number" className="font-serif text-2xl text-roast">{order?.order_number}</p>
                        </div>
                        <button
                            data-testid="order-number-copy-btn"
                            onClick={() => { navigator.clipboard?.writeText(order?.order_number || ""); toast.success("Order number copied!"); }}
                            className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-caramel transition-transform duration-200 hover:-translate-y-0.5 hover:bg-cream-100 focus:outline-none focus:ring-2 focus:ring-banana focus:ring-offset-2 focus:ring-offset-cream-50"
                        >
                            <Copy size={15} /> Copy
                        </button>
                    </div>

                    {/* Timeline */}
                    <div className="sp-anim-in" style={{ animationDelay: "200ms" }} aria-live="polite" data-testid="order-status-timeline">
                        <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-caramel">
                            <FIcon size={15} /> <span className="capitalize">{order?.fulfillment}</span> · what happens next
                        </div>
                        <ol className="relative ml-3 border-l border-dashed border-cream-300">
                            {STEPS.map((s, i) => {
                                const done = i <= active;
                                const Icon = s.icon;
                                return (
                                    <li key={s.key} className="mb-6 ml-6 last:mb-0" data-testid={`timeline-step-${s.key}`}>
                                        <span className={`absolute -left-[13px] grid h-6 w-6 place-items-center rounded-full ring-4 ring-cream-50 transition-colors ${done ? "bg-banana text-roast" : "bg-cream-300 text-mocha"}`}>
                                            <Icon size={13} strokeWidth={2.4} />
                                        </span>
                                        <p className={`font-medium ${done ? "text-roast" : "text-mocha"}`}>{s.label}</p>
                                        <p className="text-sm text-mocha">{s.note}</p>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>

                    {/* CTAs */}
                    <div className="sp-anim-in flex flex-col gap-3 sm:flex-row" style={{ animationDelay: "280ms" }}>
                        <Link to={`/track?order=${order?.order_number || ""}`} data-testid="track-order-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-banana px-6 py-3.5 font-semibold text-roast transition-colors duration-200 hover:bg-banana-hover focus:outline-none focus:ring-2 focus:ring-banana focus:ring-offset-2 focus:ring-offset-cream-50">
                            <Package size={18} /> Track my order
                        </Link>
                        <Link to="/shop" data-testid="keep-shopping-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-roast/25 px-6 py-3.5 font-semibold text-roast transition-colors duration-200 hover:bg-cream-200">
                            <ShoppingBag size={18} /> Keep shopping
                        </Link>
                    </div>
                </div>

                {/* RIGHT — receipt */}
                <aside className="grain col-span-5 border-t border-cream-200 bg-cream-100 px-6 py-14 sm:px-10 lg:border-l lg:border-t-0 lg:py-20 lg:pl-12 lg:pr-24">
                    <div className="sp-anim-in" style={{ animationDelay: "160ms" }}>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-caramel">Order summary</p>
                        <div className="mt-5 space-y-4">
                            {items.map((it, idx) => (
                                <div key={idx} className="flex gap-3" data-testid={`receipt-item-${idx}`}>
                                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-cream-300">
                                        {it.image && <img src={it.image} alt="" className="h-full w-full object-cover" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-serif text-[15px] leading-snug text-roast">{it.name}</p>
                                        <p className="text-xs text-mocha">Qty {it.quantity} · ${Number(it.unit_price).toFixed(2)} each</p>
                                    </div>
                                    <p className="whitespace-nowrap font-medium text-roast">${Number(it.line_total).toFixed(2)}</p>
                                </div>
                            ))}
                        </div>

                        <div className="my-6 border-t border-dashed border-cream-300" />

                        <dl className="space-y-2 text-sm">
                            <Row label="Subtotal" value={`$${Number(order?.subtotal || 0).toFixed(2)}`} />
                            {order?.discount > 0 && <Row label="Discount" value={`−$${Number(order.discount).toFixed(2)}`} accent />}
                            <Row label="Shipping" value={order?.shipping ? `$${Number(order.shipping).toFixed(2)}` : "Free"} />
                        </dl>

                        <div className="my-5 border-t border-dashed border-cream-300" />

                        <div className="flex items-baseline justify-between" data-testid="receipt-total">
                            <span className="font-serif text-lg text-roast">Total paid</span>
                            <span className="font-serif text-3xl text-roast">${Number(order?.total || 0).toFixed(2)}</span>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

const Row = ({ label, value, accent }) => (
    <div className="flex justify-between">
        <dt className="text-mocha">{label}</dt>
        <dd className={accent ? "font-medium text-caramel" : "text-roast"}>{value}</dd>
    </div>
);

function Skeleton() {
    return (
        <div className="min-h-screen bg-cream-50 lg:grid lg:grid-cols-12" data-testid="payment-success-loading">
            <div className="col-span-7 flex flex-col justify-center gap-6 px-6 py-16 sm:px-10 lg:pl-24 lg:pr-16">
                <div className="skeleton h-16 w-16 rounded-full" />
                <div className="skeleton h-10 w-3/4 rounded-xl" />
                <div className="skeleton h-5 w-1/2 rounded-lg" />
                <div className="skeleton h-20 w-full rounded-2xl" />
                <div className="skeleton h-48 w-full rounded-2xl" />
            </div>
            <aside className="col-span-5 space-y-4 bg-cream-100 px-6 py-16 sm:px-10 lg:pl-12 lg:pr-24">
                <div className="skeleton h-16 w-full rounded-xl" />
                <div className="skeleton h-16 w-full rounded-xl" />
                <div className="skeleton h-24 w-full rounded-xl" />
            </aside>
        </div>
    );
}

function ErrorState() {
    return (
        <div className="grid min-h-[80vh] place-items-center px-5 py-16" data-testid="payment-success-error">
            <div className="max-w-md text-center">
                <h1 className="font-serif text-3xl text-roast">We couldn't confirm this order</h1>
                <p className="mt-2 text-mocha">If you were charged, your order is safe — check your account or contact us and we'll sort it out right away.</p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                    <Link to="/track" className="inline-flex items-center justify-center rounded-full bg-banana px-6 py-3 font-semibold text-roast hover:bg-banana-hover">Track an order</Link>
                    <Link to="/" className="inline-flex items-center justify-center rounded-full border border-roast/25 px-6 py-3 font-semibold text-roast hover:bg-cream-200">Back home</Link>
                </div>
            </div>
        </div>
    );
}
